import clsx from 'clsx';
import { useFormik, FormikContext, Field, Form } from 'formik';
import { useCallback, useMemo } from 'react';

import type { DateTime } from 'luxon';
import { /* CheckboxWithLabel, */ TextField } from 'formik-material-ui';
import { KeyboardDatePicker } from 'formik-material-ui-pickers';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import ArrowForward from '@material-ui/icons/ArrowForward';
import ArrowBack from '@material-ui/icons/ArrowBack';
// import FormHelperText from '@material-ui/core/FormHelperText';

import { CalendarScheduledIcon } from '../../../assets/icons';
import useStyles from '../styles';
import { useStepperContext } from '../hooks/useStepper';
import { SecondStepSchema, VaccineValues } from '../validation';
// import { useAuth } from '../../../contexts/auth';
import { VaccinationCampaign } from '../../../services/models/vaccination';
import { dayjs, removeTime } from '../../../utils/functions/date';

interface VaccineSecondStepProps {
  campaign?: VaccinationCampaign;
  taken?: { scheduledDate: Date }[];
}

function VaccineSecondStep({ campaign, taken }: VaccineSecondStepProps) {
  const classes = useStyles();
  // const { user } = useAuth();
  const { handleNext, handlePrev, commit, data } =
    useStepperContext<VaccineValues>();

  const handleSubmit = useCallback(
    (formData: Partial<VaccineValues>) => {
      commit(formData);
      handleNext();
    },
    [commit, handleNext]
  );

  const formik = useFormik<Partial<VaccineValues>>({
    validationSchema: SecondStepSchema,
    initialValues: {
      date:
        data?.date ??
        campaign?.vaccinationSchedule.find((schedule) =>
          dayjs(schedule.startDate).isAfter(removeTime(new Date()))
        )?.startDate ??
        new Date(),
      hour: data?.hour ?? ('' as any),
      // check: data?.check ?? false,
    },
    onSubmit: handleSubmit,
  });

  const { /* errors, touched, */ isValid, isSubmitting, values, initialValues } =
    formik;

  const isDisabled = useMemo(() => {
    if (values === initialValues && (values.hour as any) === '') {
      return true;
    }

    if (!isValid) {
      return true;
    }

    if (isSubmitting) {
      return true;
    }

    return false;
  }, [values, initialValues, isValid, isSubmitting]);

  const shouldDisableDate = useCallback(
    (day: DateTime) => {
      const date = day.toJSDate();

      if (campaign?.vaccinationSchedule) {
        for (const schedule of campaign?.vaccinationSchedule) {
          if (dayjs(schedule.startDate).isSame(date, 'day')) {
            return false;
          }
        }
      }

      return true;
    },
    [campaign?.vaccinationSchedule]
  );

  const renderTime = useCallback(() => {
    if (values?.date) {
      const schedule = campaign?.vaccinationSchedule.find((schedule) =>
        dayjs(schedule.startDate).isSame(values?.date ?? new Date(), 'day')
      );

      if (schedule) {
        const items: React.ReactNodeArray = [];
        const start = dayjs(schedule.startDate);
        const end = dayjs(schedule.endDate).add(schedule.interval, 'minute');

        const minutes = end.diff(start, 'minutes');

        for (let i = 0; i < minutes / schedule.interval; i++) {
          const clone = start.add(i * schedule.interval, 'minute');

          const iso = clone.toISOString();
          // eslint-disable-next-line no-loop-func
          const disabled = taken?.some((time) => {
            return dayjs(time.scheduledDate).isSame(clone);
          });

          items.push(
            <MenuItem disabled={disabled} key={iso} value={iso}>
              {clone.format('HH:mm')}
            </MenuItem>
          );
        }

        return items;
      }

      return [];
    }

    return [];
  }, [campaign, values.date, taken]);

  return (
    <FormikContext.Provider value={formik}>
      <Form>
        <Grid container direction="column">
          <Grid item>
            <Box px={2} className={classes.disclaimer}>
              <img src={CalendarScheduledIcon} alt="calendar-scheduled-icon" />
              <Typography variant="h1">Quando quer ir?</Typography>
              <Typography>
                Selecione a melhor data, hora e local para receber a vacina.
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            <Box display="flex" justifyContent="center">
              <Box maxWidth="500px">
                <Field
                  component={KeyboardDatePicker}
                  invalidDateMessage="Data inválida"
                  minDateMessage="Data indisponível"
                  id="date"
                  name="date"
                  label="Data"
                  placeholder="Selecionar Data"
                  size="small"
                  margin="normal"
                  cancelLabel="Cancelar"
                  shouldDisableDate={shouldDisableDate}
                  KeyboardButtonProps={{ size: 'small' }}
                  disablePast
                  format="dd/MM/yyyy"
                  InputProps={{
                    classes: {
                      root: clsx(classes.input, classes.inputText),
                    },
                  }}
                />
                <Field
                  component={TextField}
                  id="hour"
                  name="hour"
                  label="Horário"
                  placeholder="Selecionar Horário"
                  size="small"
                  margin="normal"
                  select
                  InputProps={{
                    classes: {
                      root: classes.input,
                    },
                  }}
                >
                  {renderTime()}
                </Field>
                {/* <Field
                  component={CheckboxWithLabel}
                  type="checkbox"
                  name="check"
                  color="primary"
                  Label={{
                    classes: {
                      root: classes.checkbox,
                      label: classes.checkboxText,
                    },
                    label: (
                      <>
                        Eu <strong>{user.firstName}</strong>, estou de acordo
                        com os termos de privacidade e politicas relacionadas a
                        vacinação contra H1N1.
                      </>
                    ),
                  }}
                />
                {!!errors.check && !!touched.check && (
                  <Box ml={1.75}>
                    <FormHelperText error>{errors.check}</FormHelperText>
                  </Box>
                )} */}
              </Box>
            </Box>
          </Grid>
          <Grid item>
            <Box textAlign="center" py={3}>
              <Button
                startIcon={<ArrowBack />}
                variant="text"
                color="primary"
                disableElevation
                className={classes.button}
                onClick={() => handlePrev()}
              >
                Voltar
              </Button>{' '}
              <Button
                endIcon={<ArrowForward />}
                variant="contained"
                color="primary"
                type="submit"
                disableElevation
                className={classes.button}
                disabled={isDisabled}
              >
                Próximo
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Form>
    </FormikContext.Provider>
  );
}

export default VaccineSecondStep;
